import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CaseMessages from "@/components/CaseMessages";
import { ComplianceChecklist } from "@/components/ComplianceChecklist";
import { TaxAgent } from "@/components/TaxAgent";
import { StaffReceipts } from "@/components/StaffReceipts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, FileText, Download, Upload, ClipboardList, Plus, CheckCircle2, XCircle, Eye, EyeOff,
} from "lucide-react";

const STATUSES = ["Intake", "Review", "Filed", "Complete"];
const REQ_BADGE = {
  requested: "bg-[#FEF3C7] text-[#92600A]",
  uploaded: "bg-[#DBEAFE] text-[#1E40AF]",
  approved: "bg-[#DCFCE7] text-[#166534]",
  rejected: "bg-[#FEE2E2] text-[#B91C1C]",
};

export default function CaseWorkspace() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState({});
  const [customLabel, setCustomLabel] = useState("");
  const load = () => {
    api.get(`/staff/cases/${caseId}`).then(({ data }) => {
      setC(data);
      const map = {};
      (data.doc_requests || []).forEach((r) => { map[r.label] = true; });
      setChecked((prev) => ({ ...map, ...prev }));
    }).catch(() => toast.error("Case not found or no access")).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user && (user.role === "tech" || user.role === "admin")) {
      api.get("/staff/doc-catalog").then(({ data }) => setCatalog(data.standard)).catch(() => {});
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, caseId]);

  if (user && user.role !== "tech" && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const download = async (path, filename) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download file"); }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/staff/cases/${caseId}/status`, { status });
      setC((p) => ({ ...p, status }));
      toast.success(`Moved to ${status}`);
    } catch { toast.error("Could not update status"); } finally { setBusy(false); }
  };

  const saveRequests = async () => {
    const items = [];
    catalog.forEach((s) => { if (checked[s.label]) items.push({ key: s.key, label: s.label, required: true }); });
    (c.doc_requests || []).forEach((r) => {
      if (!r.key && checked[r.label] && !items.find((i) => i.label === r.label))
        items.push({ key: null, label: r.label, required: true });
    });
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/cases/${caseId}/doc-requests`, { items });
      setC((p) => ({ ...p, doc_requests: data.doc_requests }));
      toast.success("Document checklist updated — client can now see it");
    } catch { toast.error("Could not update checklist"); } finally { setBusy(false); }
  };

  const addCustom = () => {
    const label = customLabel.trim();
    if (!label) return;
    setChecked((p) => ({ ...p, [label]: true }));
    setC((p) => ({ ...p, doc_requests: [...(p.doc_requests || []), { id: `tmp_${label}`, key: null, label, required: true, status: "requested" }] }));
    setCustomLabel("");
  };

  const setReqStatus = async (reqId, status) => {
    try {
      const { data } = await api.post(`/staff/cases/${caseId}/doc-requests/${reqId}/status`, { status });
      setC((p) => ({ ...p, doc_requests: data.doc_requests }));
    } catch { toast.error("Could not update"); }
  };

  const deliver = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = ".pdf,.png,.jpg,.jpeg,.doc,.docx";
    input.onchange = async (e) => {
      const file = e.target.files?.[0]; if (!file) return;
      const fd = new FormData(); fd.append("file", file);
      setBusy(true);
      try {
        await api.post(`/staff/cases/${caseId}/deliver`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Delivered to client"); load();
      } catch { toast.error("Could not deliver"); } finally { setBusy(false); }
    };
    input.click();
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9F9F8]"><Navbar />
      <div className="pt-40 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#114A31]" /></div>
    </div>
  );
  if (!c) return (
    <div className="min-h-screen bg-[#F9F9F8]"><Navbar />
      <div className="pt-40 text-center text-[#595D5A]">Case not found.</div>
    </div>
  );

  const allLabels = [...catalog.map((s) => s.label), ...(c.doc_requests || []).filter((r) => !r.key).map((r) => r.label)];
  const uniqueLabels = [...new Set(allLabels)];
  const reqByLabel = Object.fromEntries((c.doc_requests || []).map((r) => [r.label, r]));

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 sm:px-12 pt-28 pb-20">
        <button onClick={() => navigate(user.role === "admin" ? "/admin" : "/tech")} data-testid="ws-back"
          className="inline-flex items-center gap-1.5 text-sm text-[#595D5A] hover:text-[#114A31]">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl tracking-tight font-medium text-[#1C1C1C]">{c.business_name}</h1>
            <p className="mt-1 text-[#595D5A]">{c.user_name} · {c.user_email} · {c.entity_state}</p>
            <p className="text-sm text-[#595D5A]">Owners: {c.num_owners} · Net profit: {c.annual_revenue} · Payroll: {c.current_payroll}</p>
          </div>
          <div className="w-48">
            <label className="text-xs text-[#595D5A]">Case status</label>
            <Select value={c.status} onValueChange={changeStatus} disabled={busy}>
              <SelectTrigger className="mt-1 rounded-xl border-[#E5E5E5] bg-white" data-testid="ws-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{!STATUSES.includes(c.status) && <SelectItem value={c.status} disabled>{c.status}</SelectItem>}{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Document checklist */}
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="ws-doc-requests">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-[#114A31]" />
              <h2 className="font-heading font-semibold text-[#1C1C1C]">Request documents</h2>
            </div>
            <p className="text-xs text-[#595D5A] mt-1">Select what this client needs to provide. They'll see the checklist and upload against it.</p>
            <div className="mt-4 space-y-2">
              {uniqueLabels.map((label) => {
                const r = reqByLabel[label];
                return (
                  <div key={label} className="flex items-center gap-3 rounded-xl border border-[#E5E5E5] px-3 py-2">
                    <Checkbox checked={!!checked[label]} onCheckedChange={(v) => setChecked((p) => ({ ...p, [label]: !!v }))}
                      data-testid={`ws-req-check-${label}`} className="border-[#114A31] data-[state=checked]:bg-[#114A31]" />
                    <span className="flex-1 text-sm text-[#1C1C1C]">{label}</span>
                    {r && (
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${REQ_BADGE[r.status] || ""}`}>{r.status}</span>
                    )}
                    {r && r.status === "uploaded" && (
                      <>
                        <button onClick={() => setReqStatus(r.id, "approved")} title="Approve" data-testid={`ws-approve-${r.id}`} className="text-[#166534] hover:opacity-70"><CheckCircle2 className="h-4 w-4" /></button>
                        <button onClick={() => setReqStatus(r.id, "rejected")} title="Request again" className="text-[#B91C1C] hover:opacity-70"><XCircle className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="Add a custom document…"
                data-testid="ws-custom-input" className="rounded-xl border-[#E5E5E5]" />
              <Button onClick={addCustom} variant="outline" className="rounded-xl border-[#E5E5E5] shrink-0"><Plus className="h-4 w-4" /></Button>
            </div>
            <Button onClick={saveRequests} disabled={busy} data-testid="ws-save-requests"
              className="mt-4 w-full rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">Save checklist for client</Button>
          </div>

          {/* Files */}
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="ws-files">
            <h2 className="font-heading font-semibold text-[#1C1C1C]">Client documents</h2>
            {c.files?.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {c.files.map((f) => (
                  <li key={f.file_id} className="flex items-center gap-2 text-sm bg-[#F9F9F8] rounded-xl px-3 py-2">
                    <FileText className="h-4 w-4 text-[#114A31]" />
                    <span className="flex-1 truncate text-[#1C1C1C]">{f.original_filename}</span>
                    <button onClick={() => download(f.storage_path, f.original_filename)} data-testid={`ws-dl-${f.file_id}`} className="text-[#595D5A] hover:text-[#114A31]"><Download className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-3 text-sm text-[#595D5A]">No documents uploaded yet.</p>}

            <div className="mt-5 pt-5 border-t border-[#E5E5E5]">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-[#1C1C1C]">Delivered to client</h2>
                <Button onClick={deliver} disabled={busy} variant="outline" data-testid="ws-deliver-btn"
                  className="rounded-full border-[#E5E5E5] hover:border-[#114A31] text-[#1C1C1C]"><Upload className="h-4 w-4 mr-2" /> Deliver</Button>
              </div>
              {c.delivered_files?.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="ws-shared-list">
                  {c.delivered_files.map((f) => (
                    <li key={f.file_id} className="flex items-center gap-2 text-sm bg-[#E6F0EB] rounded-xl px-3 py-2">
                      <FileText className="h-4 w-4 text-[#114A31]" />
                      <span className="flex-1 truncate text-[#114A31]">{f.original_filename}</span>
                      {f.seen_by_client ? (
                        <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#166534]" title={f.seen_at ? `Opened ${new Date(f.seen_at).toLocaleString()}` : "Client has opened this"} data-testid={`ws-shared-seen-${f.file_id}`}>
                          <Eye className="h-3 w-3" /> {f.seen_at ? `Opened ${new Date(f.seen_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${new Date(f.seen_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : "Viewed"}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#92600A]" title="Client hasn't opened this yet" data-testid={`ws-shared-unseen-${f.file_id}`}><EyeOff className="h-3 w-3" /> Not opened</span>
                      )}
                      <button onClick={() => download(f.storage_path, f.original_filename)} className="text-[#114A31] hover:opacity-70"><Download className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <StaffReceipts caseId={caseId} />
        </div>

        <div className="mt-6">
          <TaxAgent caseId={caseId} clientEmail={c.user_email} />
        </div>

        <div className="mt-6">
          <ComplianceChecklist scope="staff" basePath={`/staff/cases/${caseId}/compliance`} testId="staff-compliance" />
        </div>

        <div className="mt-6">
          <CaseMessages caseId={caseId} />
        </div>
      </div>
    </div>
  );
}
